import { FULL_DECK } from '../core/deck';
import { bestHand, compareHands } from '../core/eval';
import { COMBO_CARDS, COMBO_COUNT } from '../range/combos';
/** C(n,k) as an integer (round-off-safe for our small n,k). 0 when k out of [0,n]. */
export const nCk = (n, k) => {
    if (k < 0 || k > n)
        return 0;
    if (k === 0 || k === n)
        return 1;
    const kk = Math.min(k, n - k);
    let r = 1;
    for (let i = 0; i < kk; i++)
        r = (r * (n - i)) / (i + 1);
    return Math.round(r);
};
/** The 52-card deck minus the dead cards (board + known holes). Fresh array. */
export const remainingDeck = (dead) => {
    const s = new Set(dead);
    return FULL_DECK.filter((c) => !s.has(c));
};
/**
 * Enumerate every `slots`-card runout drawn from `deck`, invoking `cb` with each combination. The
 * array passed to `cb` is REUSED between calls (do not retain it). `slots === 0` invokes `cb([])` once
 * (the board is already complete). O(C(deck.length, slots)) calls.
 */
export const forEachRunout = (deck, slots, cb) => {
    if (slots === 0) {
        cb([]);
        return;
    }
    const pick = new Array(slots);
    const rec = (start, depth) => {
        if (depth === slots) {
            cb(pick);
            return;
        }
        const end = deck.length - (slots - depth);
        for (let i = start; i <= end; i++) {
            pick[depth] = deck[i];
            rec(i + 1, depth + 1);
        }
    };
    rec(0, 0);
};
/** Hero's outcome on a complete 5-card board vs one villain: 1 win / 0.5 chop / 0 loss. */
const heroOutcome = (hero, villain, fullBoard) => {
    const c = compareHands(bestHand(hero, fullBoard), bestHand(villain, fullBoard));
    return c === 1 ? 1 : c === 0 ? 0.5 : 0;
};
/**
 * Exact hero-vs-villain equity over every remaining runout. No RNG, no sampling - the mean over the
 * full runout enumeration. `board` may be 0/3/4/5 cards; slots = 5 − board.length.
 */
export const handVsHandExact = (hero, villain, board) => {
    const slots = 5 - board.length;
    const deck = remainingDeck([...board, ...hero, ...villain]);
    let sum = 0;
    let n = 0;
    forEachRunout(deck, slots, (runout) => {
        const fb = runout.length === 0 ? board : [...board, ...runout];
        sum += heroOutcome(hero, villain, fb);
        n += 1;
    });
    return n === 0 ? 0 : sum / n;
};
/**
 * Exact hero-vs-range equity: weighted mean of hero-vs-combo exact equity over the range's live
 * combos. Combos that share a card with hero or the board are skipped (dead), so removal need not be
 * pre-applied - but passing an already-removed range is cheaper. Returns 0 for an empty range.
 */
export const handVsRangeExact = (hero, range, board) => {
    const w = range.weights;
    const slots = 5 - board.length;
    const blocked = new Set([...hero, ...board]);
    let num = 0;
    let den = 0;
    for (let idx = 0; idx < COMBO_COUNT; idx++) {
        const weight = w[idx];
        if (weight <= 1e-9)
            continue;
        const [a, b] = COMBO_CARDS[idx];
        if (blocked.has(a) || blocked.has(b))
            continue;
        const villain = [a, b];
        const deck = remainingDeck([...board, ...hero, a, b]);
        let sum = 0;
        let n = 0;
        forEachRunout(deck, slots, (runout) => {
            const fb = runout.length === 0 ? board : [...board, ...runout];
            sum += heroOutcome(hero, villain, fb);
            n += 1;
        });
        if (n > 0) {
            num += weight * (sum / n);
            den += weight;
        }
    }
    return den === 0 ? 0 : num / den;
};
//# sourceMappingURL=enumerate.js.map